/**
 * Action Form Backend API Service
 *
 * All Action-form-specific API calls. Mirrors the pattern in nps.js / Backend.js.
 * See: context/action_form_api_contract.md for full endpoint docs.
 */

import { API_BASE_URL } from '../config';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();
monday.setApiVersion('2026-01');

const addActionForm = async (sessionToken, boardId) => {
  let res = await fetch(API_BASE_URL + '/form_action/add/' + encodeURIComponent(sessionToken), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ board_id: boardId }),
  });
  return await res.json();
};

const getActionForm = async (sessionToken, formId) => {
  let res = await fetch(API_BASE_URL + '/form_action/get/' + formId + '/' + encodeURIComponent(sessionToken));
  return await res.json();
};

const updateActionForm = async (sessionToken, payload) => {
  let res = await fetch(API_BASE_URL + '/form_action/update/' + encodeURIComponent(sessionToken), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
};

const updateActionLogo = async (sessionToken, formId, formData) => {
  let res = await fetch(API_BASE_URL + '/form_action/update-logo/' + formId + '/' + encodeURIComponent(sessionToken), {
    method: 'POST',
    body: formData,
  });
  return await res.json();
};

const toggleActionFormStatus = async (sessionToken, formId, enabled) => {
  let res = await fetch(API_BASE_URL + '/form_action/status/' + formId + '/' + enabled + '/' + encodeURIComponent(sessionToken), {
    method: 'PATCH',
  });
  return await res.json();
};

const deleteActionForm = async (sessionToken, formId) => {
  let res = await fetch(API_BASE_URL + '/form_action/delete/' + formId + '/' + encodeURIComponent(sessionToken), {
    method: 'DELETE',
  });
  return await res.json();
};

const getActionRecipients = async (sessionToken, formId) => {
  let res = await fetch(API_BASE_URL + '/form/action/recipients/' + formId + '/' + encodeURIComponent(sessionToken));
  return await res.json();
};

/**
 * Sync a status-label colour change to Monday's board settings.
 *
 * @param {number|string} boardId  – Target board ID
 * @param {string}   columnId      – Status column ID
 * @param {string[]} labelsPayload – Pre-built label fragments,
 *   e.g. ['{ color: grass_green, label: "Done", index: 0 }']
 * @returns {Promise<object>} Monday API response
 */
const updateStatusLabelColor = (boardId, columnId, labelsPayload, revision) => {
  return monday.api(`
    mutation {
      update_status_column(
        board_id: ${boardId},
        id: "${columnId}",
        revision: "${revision}",
        settings: {
          labels: [${labelsPayload.join(', ')}]
        }
      ) { id }
    }
  `);
};

/**
 * Fetch all columns for a board via the Monday SDK.
 *
 * @param {number|string} boardId – Target board ID
 * @returns {Promise<object[]>} Array of column objects ({ id, title, type, settings, revision })
 */
const fetchBoardColumns = async (boardId) => {
  const res = await monday.api(`query {
    boards(ids: [${boardId}]) {
      columns {
        id
        title
        type
        settings
        revision
      }
    }
  }`);
  return res?.data?.boards?.[0]?.columns || [];
};

/**
 * Fetch a single column by ID for a board via the Monday SDK.
 *
 * @param {number|string} boardId  – Target board ID
 * @param {string}        columnId – Column ID to fetch
 * @returns {Promise<object|null>} Column object ({ id, title, type, settings, revision }) or null
 */
const fetchBoardColumn = async (boardId, columnId) => {
  const res = await monday.api(`query {
    boards(ids: [${boardId}]) {
      columns(ids: ["${columnId}"]) {
        id
        title
        type
        settings
        revision
      }
    }
  }`);
  return res?.data?.boards?.[0]?.columns?.[0] || null;
};

/**
 * Upload an image for use in an email signature.
 *
 * @param {string} sessionToken – Monday session token
 * @param {File}   file         – Image file to upload (max 500KB)
 * @returns {Promise<{ ok: boolean, url: string }>}
 */
const uploadSignatureImage = async (sessionToken, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(
    API_BASE_URL + '/email/upload-signature-image/' + encodeURIComponent(sessionToken),
    { method: 'POST', body: formData },
  );
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  // Backend returns a relative path (e.g. /uploads/signatures/...).
  // Prepend API_BASE_URL so the editor inserts a fully-qualified URL.
  if (data.url && data.url.startsWith('/')) {
    data.url = API_BASE_URL + data.url;
  }
  return data;
};

export {
  monday,
  addActionForm,
  getActionForm,
  updateActionForm,
  updateActionLogo,
  toggleActionFormStatus,
  deleteActionForm,
  getActionRecipients,
  updateStatusLabelColor,
  fetchBoardColumns,
  fetchBoardColumn,
  uploadSignatureImage,
};
